// SPA entry: mounts the existing ERP App behind a real auth gate.
//
// On mount we ask the backend "who am I" (GET /auth/me). On success we render
// the existing App, passing the REAL {user, company, role} from /me as the
// `auth` prop (the shell uses it for nav gating + the current-user display).
// On 401 we render the Growthh-branded login screen, which POSTs /auth/login
// and, on success, enters the app. Logout POSTs /auth/logout and drops back to
// the login screen. All DOMAIN module data stays on the mock reducer this run.

import React, { useCallback, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import App from "../jainys-erp.jsx";
import { apiGet, apiPost, ApiError, refreshSession } from "./apiClient.js";

const PERIWINKLE = "#4F6FE8";
const NAVY = "#1A1F3C";

function Splash({ label }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        fontFamily: "'Poppins',-apple-system,'Segoe UI',Roboto,sans-serif",
        background: "#F6F7FA",
        color: "#64748b",
        fontSize: 14,
      }}
    >
      {label || "Loading…"}
    </div>
  );
}

function LoginScreen({ onAuthed, onForgot }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = useCallback(async () => {
    if (busy) return;
    setError("");
    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }
    setBusy(true);
    try {
      // POST /auth/login returns {user, company, role} (same shape as /me).
      const data = await apiPost("/auth/login", {
        email: email.trim(),
        password,
      });
      onAuthed(data);
    } catch (e) {
      const msg =
        e instanceof ApiError ? e.message : "Sign in failed. Please try again.";
      setError(msg);
      setBusy(false);
    }
  }, [email, password, busy, onAuthed]);

  const onKeyDown = (e) => {
    if (e.key === "Enter") submit();
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        fontFamily: "'Poppins',-apple-system,'Segoe UI',Roboto,sans-serif",
        background:
          "radial-gradient(1100px 600px at 70% -10%, rgba(79,111,232,0.16), transparent), #F6F7FA",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 388,
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 12px 40px rgba(26,31,60,0.12)",
          padding: 32,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
          <div
            style={{
              height: 36,
              width: 36,
              borderRadius: 10,
              background: NAVY,
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            G
          </div>
          <div>
            <div style={{ fontWeight: 600, color: NAVY, fontSize: 16, lineHeight: 1.1 }}>
              Sweet Manufacturing ERP
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>by Growthh</div>
          </div>
        </div>

        <h1 style={{ fontSize: 18, fontWeight: 600, color: "#1e293b", margin: "0 0 4px" }}>
          Sign in
        </h1>
        <p style={{ fontSize: 13, color: "#94a3b8", margin: "0 0 20px" }}>
          Use your company account to continue.
        </p>

        <label style={{ display: "block", marginBottom: 14 }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: "#64748b" }}>Email</span>
          <input
            type="email"
            value={email}
            autoFocus
            disabled={busy}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="you@company.com"
            style={inputStyle}
          />
        </label>

        <label style={{ display: "block", marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: "#64748b" }}>Password</span>
          <input
            type="password"
            value={password}
            disabled={busy}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="••••••••"
            style={inputStyle}
          />
        </label>

        {error ? (
          <div
            style={{
              marginTop: 12,
              background: "rgba(229,72,77,0.10)",
              color: "#b91c1c",
              border: "1px solid rgba(229,72,77,0.25)",
              borderRadius: 8,
              padding: "8px 11px",
              fontSize: 12.5,
            }}
          >
            {error}
          </div>
        ) : null}

        <button
          type="button"
          onClick={submit}
          disabled={busy}
          style={{
            marginTop: 18,
            width: "100%",
            border: "none",
            borderRadius: 10,
            padding: "11px 14px",
            background: busy ? "#9bb0f0" : PERIWINKLE,
            color: "#fff",
            fontWeight: 600,
            fontSize: 14,
            cursor: busy ? "default" : "pointer",
          }}
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>

        <div style={{ marginTop: 14, textAlign: "center" }}>
          <button
            type="button"
            onClick={onForgot}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              color: PERIWINKLE,
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Forgot password?
          </button>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  marginTop: 6,
  width: "100%",
  boxSizing: "border-box",
  borderRadius: 9,
  border: "1px solid #e2e8f0",
  padding: "10px 12px",
  fontSize: 14,
  color: "#1e293b",
  outline: "none",
  background: "#fff",
};

const errorBoxStyle = {
  marginTop: 12,
  background: "rgba(229,72,77,0.10)",
  color: "#b91c1c",
  border: "1px solid rgba(229,72,77,0.25)",
  borderRadius: 8,
  padding: "8px 11px",
  fontSize: 12.5,
};
const noticeBoxStyle = {
  marginTop: 12,
  background: "rgba(45,164,78,0.10)",
  color: "#15803d",
  border: "1px solid rgba(45,164,78,0.25)",
  borderRadius: 8,
  padding: "10px 12px",
  fontSize: 13,
  lineHeight: 1.5,
};
const primaryBtnStyle = (busy) => ({
  marginTop: 18,
  width: "100%",
  border: "none",
  borderRadius: 10,
  padding: "11px 14px",
  background: busy ? "#9bb0f0" : PERIWINKLE,
  color: "#fff",
  fontWeight: 600,
  fontSize: 14,
  cursor: busy ? "default" : "pointer",
});
const linkBtnStyle = {
  background: "none",
  border: "none",
  padding: 0,
  color: PERIWINKLE,
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

// Shared brand card chrome for the pre-auth screens (login / forgot / reset).
function AuthShell({ title, subtitle, children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        fontFamily: "'Poppins',-apple-system,'Segoe UI',Roboto,sans-serif",
        background:
          "radial-gradient(1100px 600px at 70% -10%, rgba(79,111,232,0.16), transparent), #F6F7FA",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 388,
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 12px 40px rgba(26,31,60,0.12)",
          padding: 32,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
          <div
            style={{
              height: 36,
              width: 36,
              borderRadius: 10,
              background: NAVY,
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            G
          </div>
          <div>
            <div style={{ fontWeight: 600, color: NAVY, fontSize: 16, lineHeight: 1.1 }}>
              Sweet Manufacturing ERP
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>by Growthh</div>
          </div>
        </div>
        <h1 style={{ fontSize: 18, fontWeight: 600, color: "#1e293b", margin: "0 0 4px" }}>{title}</h1>
        {subtitle ? (
          <p style={{ fontSize: 13, color: "#94a3b8", margin: "0 0 20px" }}>{subtitle}</p>
        ) : (
          <div style={{ height: 8 }} />
        )}
        {children}
      </div>
    </div>
  );
}

// Forgot-password: email → POST /auth/forgot-password → generic confirmation
// (never reveals whether the account exists).
function ForgotPasswordScreen({ onBack }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = useCallback(async () => {
    if (busy) return;
    setError("");
    if (!email.trim()) {
      setError("Enter your email.");
      return;
    }
    setBusy(true);
    try {
      const data = await apiPost("/auth/forgot-password", { email: email.trim() });
      setDone(data?.message || "If an account exists for that email, a reset link has been sent.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong. Please try again.");
      setBusy(false);
    }
  }, [email, busy]);

  return (
    <AuthShell
      title="Reset your password"
      subtitle={done ? null : "Enter your account email and we'll send a reset link."}
    >
      {done ? (
        <>
          <div style={noticeBoxStyle}>{done}</div>
          <div style={{ marginTop: 18, textAlign: "center" }}>
            <button type="button" onClick={onBack} style={linkBtnStyle}>← Back to sign in</button>
          </div>
        </>
      ) : (
        <>
          <label style={{ display: "block", marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: "#64748b" }}>Email</span>
            <input
              type="email"
              value={email}
              autoFocus
              disabled={busy}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="you@company.com"
              style={inputStyle}
            />
          </label>
          {error ? <div style={errorBoxStyle}>{error}</div> : null}
          <button type="button" onClick={submit} disabled={busy} style={primaryBtnStyle(busy)}>
            {busy ? "Sending…" : "Send reset link"}
          </button>
          <div style={{ marginTop: 14, textAlign: "center" }}>
            <button type="button" onClick={onBack} style={linkBtnStyle}>← Back to sign in</button>
          </div>
        </>
      )}
    </AuthShell>
  );
}

// Reset-password: reads ?token= → new password (+ confirm) →
// POST /auth/reset-password → success links to sign in.
function ResetPasswordScreen({ onToLogin }) {
  const token = (() => {
    try {
      return new URLSearchParams(window.location.search).get("token") || "";
    } catch (e) {
      return "";
    }
  })();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  const submit = useCallback(async () => {
    if (busy) return;
    setError("");
    if (pw.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (pw !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const data = await apiPost("/auth/reset-password", { token, newPassword: pw });
      setDone(data?.message || "Your password has been updated. Please sign in.");
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : "This reset link is invalid or has expired.",
      );
      setBusy(false);
    }
  }, [pw, confirm, token, busy]);

  if (!token) {
    return (
      <AuthShell title="Reset your password" subtitle={null}>
        <div style={errorBoxStyle}>This reset link is missing or invalid. Please request a new one.</div>
        <button type="button" onClick={onToLogin} style={primaryBtnStyle(false)}>Go to sign in</button>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password" subtitle={done ? null : "Enter and confirm your new password."}>
      {done ? (
        <>
          <div style={noticeBoxStyle}>{done}</div>
          <button type="button" onClick={onToLogin} style={primaryBtnStyle(false)}>Go to sign in</button>
        </>
      ) : (
        <>
          <label style={{ display: "block", marginBottom: 14 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: "#64748b" }}>New password</span>
            <input
              type="password"
              value={pw}
              autoFocus
              disabled={busy}
              onChange={(e) => setPw(e.target.value)}
              placeholder="At least 8 characters"
              style={inputStyle}
            />
          </label>
          <label style={{ display: "block", marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: "#64748b" }}>Confirm new password</span>
            <input
              type="password"
              value={confirm}
              disabled={busy}
              onChange={(e) => setConfirm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="Re-enter password"
              style={inputStyle}
            />
          </label>
          {error ? (
            <div style={errorBoxStyle}>
              {error}
              {" "}
              <button type="button" onClick={onToLogin} style={{ ...linkBtnStyle, fontSize: 12.5 }}>
                Request a new link
              </button>
            </div>
          ) : null}
          <button type="button" onClick={submit} disabled={busy} style={primaryBtnStyle(busy)}>
            {busy ? "Updating…" : "Update password"}
          </button>
        </>
      )}
    </AuthShell>
  );
}

function Root() {
  const [status, setStatus] = useState("loading"); // loading | authed | anon
  const [auth, setAuth] = useState(null);
  const [anonView, setAnonView] = useState("login"); // login | forgot
  // Pre-auth route: /reset-password is reached from the emailed link (SPA
  // fallback serves index.html). It runs independently of the session.
  const isResetRoute =
    typeof window !== "undefined" && window.location.pathname === "/reset-password";

  const goLogin = useCallback(() => {
    // Drop the ?token / path from the URL and return to the login screen.
    try {
      window.history.replaceState({}, "", "/");
    } catch (e) {}
    setAnonView("login");
    setStatus("anon");
    setAuth(null);
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const me = await apiGet("/auth/me");
      setAuth(me);
      setStatus("authed");
      return;
    } catch (e) {
      // /auth/me failed. Do NOT conclude "logged out" yet: the access cookie
      // lives 15 minutes but the refresh cookie lives 30 days, so on a reload
      // after a short idle the session is almost always still renewable. This
      // is the "refreshing the page logs me out" bug — the app used to give up
      // here without ever asking.
      if (!(e instanceof ApiError) || e.status !== 401) {
        setAuth(null);
        setStatus("anon");
        return;
      }
    }
    try {
      const renewed = await refreshSession();
      if (renewed) {
        const me = await apiGet("/auth/me");
        setAuth(me);
        setStatus("authed");
        return;
      }
    } catch (e) {
      // fall through to the login screen
    }
    // Refresh cookie missing/expired/revoked (or a real logout) => genuinely anon.
    setAuth(null);
    setStatus("anon");
  }, []);

  useEffect(() => {
    if (isResetRoute) return; // skip the session probe on the reset route
    loadMe();
  }, [loadMe, isResetRoute]);

  const onLogout = useCallback(async () => {
    try {
      await apiPost("/auth/logout");
    } catch (e) {
      // Even if the call fails, drop the client session.
    }
    setAuth(null);
    setStatus("anon");
    setAnonView("login");
  }, []);

  // Reset-password view takes precedence (it carries its own token).
  if (isResetRoute) return <ResetPasswordScreen onToLogin={goLogin} />;

  if (status === "loading") return <Splash label="Loading…" />;
  if (status === "anon" || !auth) {
    if (anonView === "forgot")
      return <ForgotPasswordScreen onBack={() => setAnonView("login")} />;
    return (
      <LoginScreen
        onAuthed={(data) => {
          setAuth(data);
          setStatus("authed");
        }}
        onForgot={() => setAnonView("forgot")}
      />
    );
  }

  return <App auth={auth} onLogout={onLogout} />;
}

const rootEl = document.getElementById("root");
createRoot(rootEl).render(<Root />);
